/**
 * Award-data SSOT — Compliance constants (24/7 RN + civil penalties)
 *
 * Aged Care Act 2024 + Aged Care Rules 2025 enforcement framework data.
 *
 * NOTE: This is regulator/legislative data, NOT a Fair Work modern award.
 * Sits in `award-data/` for SSOT discipline consistency.
 */

// ═══════════════════════════════════════════════════════════════════════
// 24/7 Registered Nurse on-site requirement
// ═══════════════════════════════════════════════════════════════════════

/**
 * Aged Care Act 2024 s 175 (24/7 registered nurse responsibility) + s 176
 * (care minutes responsibility), Aged Care Rules 2025 rr 176-15, 176-20
 * (care minutes) and Chapter 5 (Quality Standard 5 — Clinical Care).
 * Carried forward from Aged Care Act 1997 s 54-1A (2022 Royal Commission
 * response). Applies to every approved residential care home; exemptions
 * granted by the Secretary (delegated from the Minister) for small
 * rural/remote services, time-limited (typically 12 months, renewable).
 *
 * The requirement is physical on-site presence of an AHPRA-registered
 * Registered Nurse (Division 1). Enrolled nurses do not satisfy the
 * requirement; on-call RNs do not satisfy the requirement; rostered RNs
 * on leave do not count for those minutes.
 *
 * Monthly reporting: the report required under Act s 166(1)(d) in relation
 * to the s 175(1) obligation is due within 7 days after the end of each
 * calendar month (Aged Care Rules 2025 r 166-855(1)) — this is a binding
 * Rule, not departmental guidance — "or within a longer period specified
 * by the System Governor by notice in writing given to the provider."
 * A provider holding an exemption must still report.
 *
 * Source: Aged Care Act 2024 s 175, s 176; Aged Care Rules 2025
 * r 166-855(1), rr 176-15, 176-20.
 * Verified: 2026-09-21 with aged-care-legislation specialist. See
 * docs/operations/clause-verifications/2026-09-21-aged-care-legislation-compliance-module-legal-correctness.md
 */

/** Seconds in a day — threshold for full 24/7 compliance. */
export const RN_24_7_REQUIREMENT_DAILY_SECONDS = 24 * 60 * 60;

/**
 * Deadline day-of-month for the GPMS monthly 24/7 Nursing Responsibility
 * Report (Aged Care Rules 2025 r 166-855(1)). The Rule allows the System
 * Governor to specify a longer period by written notice to the provider —
 * this constant is the default statutory day and does not itself model an
 * extension; a provider-specific override belongs in tenant data, not here.
 */
export const RN_24_7_MONTHLY_REPORT_DAY = 7;

// ═══════════════════════════════════════════════════════════════════════
// Commonwealth penalty unit + civil penalty caps
// ═══════════════════════════════════════════════════════════════════════

/**
 * Civil penalty amounts under the Aged Care Act 2024 are expressed in
 * "penalty units". Each unit equals a dollar amount set under Crimes Act
 * 1914 (Cth) s 4AA(1A), indexed periodically.
 *
 * Indexation history (each `unit` applies to conduct/offences occurring on
 * or after `effectiveFrom`, per s 4AA(8), until superseded by the next
 * entry):
 *   - $330 per unit: 7 Nov 2024 – 30 Jun 2026 (indexed from $313 on Jul 2023).
 *   - $364 per unit: from 1 Jul 2026 — Crimes (Amount of a Penalty Unit)
 *     Instrument 2026 (F2026N00424) s 5, made under s 4AA(1A):
 *     "For the purposes of subsection 4AA(1A) of the Act, the amount of a
 *     penalty unit is $364 ... only applies to offences committed on or
 *     after 1 July 2026 (see subsection 4AA(8) of the Act)."
 *
 * Reference: Crimes Act 1914 (Cth) s 4AA; Crimes (Amount of a Penalty Unit)
 * Instrument 2026 (F2026N00424) s 5.
 * Verified: 2026-09-21 with aged-care-legislation specialist. See
 * docs/operations/clause-verifications/2026-09-21-aged-care-legislation-compliance-module-legal-correctness.md
 */
export const COMMONWEALTH_PENALTY_UNIT_HISTORY: ReadonlyArray<{
  /** ISO date (inclusive) from which this unit value applies. */
  effectiveFrom: string;
  /** Dollar value of one Commonwealth penalty unit from that date. */
  unit: number;
}> = [
  { effectiveFrom: '2024-11-07', unit: 330 },
  { effectiveFrom: '2026-07-01', unit: 364 },
];

/**
 * Returns the Commonwealth penalty unit dollar value applicable to conduct
 * occurring on the given date (Crimes Act 1914 s 4AA(8) — the indexed
 * amount applies to offences committed on or after the instrument's
 * effective date). Use this instead of the bare `COMMONWEALTH_PENALTY_UNIT`
 * constant whenever the conduct date may fall before 1 July 2026.
 */
export function commonwealthPenaltyUnitFor(date: Date): number {
  // This compares a Commonwealth-instrument commencement date (a calendar
  // date fixed by the Federal Register, not a tenant business day) against
  // a conduct date; it is not a tenant-timezone business-date derivation.
  // eslint-disable-next-line statura/no-utc-date-string -- Commonwealth instrument date, not a tenant business date
  const iso = date.toISOString().slice(0, 10);
  // Default to the earliest known indexation ($330, 7 Nov 2024) rather than
  // indexing [0] directly, which TS flags as possibly undefined under
  // noUncheckedIndexedAccess even though this array literal is non-empty.
  let unit = 330;
  for (const entry of COMMONWEALTH_PENALTY_UNIT_HISTORY) {
    if (iso >= entry.effectiveFrom) {
      unit = entry.unit;
    }
  }
  return unit;
}

/** Current Commonwealth penalty unit — $364, applicable to conduct on or
 *  after 1 July 2026. For a conduct date that may precede 1 July 2026, use
 *  `commonwealthPenaltyUnitFor(date)` instead. */
export const COMMONWEALTH_PENALTY_UNIT = 364;

/**
 * Maximum civil penalty for a registered provider that is not an individual
 * (a "body corporate" in the ordinary sense) under the Aged Care Act 2024:
 * 4,800 penalty units — Act s 179(5)(b) (serious failure of the s 179(1)
 * duty resulting in death, serious injury or illness of an individual to
 * whom the duty is owed):
 * "Civil penalty: ... (b) in the case of a contravention by a registered
 * provider other than an individual—4,800 penalty units."
 * There is no Regulatory Powers Act multiplier here — s 179(5) states
 * separate explicit figures for an individual vs. a non-individual
 * registered provider, displacing the generic 5x body-corporate multiplier.
 * 4,800 × $364 (from 1 Jul 2026) = $1,747,200.
 */
// eslint-disable-next-line statura/no-floating-point-money -- integer × integer (unit values are always whole dollars per s 4AA indexation; no IEEE-754 risk)
export const MAX_CIVIL_PENALTY_BODY_CORPORATE = 4_800 * COMMONWEALTH_PENALTY_UNIT;

/**
 * Returns the s 179(5)(b) maximum civil penalty for a registered provider
 * other than an individual, in dollars, for conduct on the given date.
 */
export function maxCivilPenaltyBodyCorporateFor(date: Date): number {
  return 4_800 * commonwealthPenaltyUnitFor(date);
}

/**
 * Maximum civil penalty for a registered provider that is an individual
 * under the Aged Care Act 2024: 500 penalty units — Act s 179(5)(a)
 * (serious failure of the s 179(1) duty resulting in death, serious injury
 * or illness of an individual to whom the duty is owed):
 * "Civil penalty: (a) in the case of a contravention by a registered
 * provider that is an individual—500 penalty units."
 * 500 × $364 (from 1 Jul 2026) = $182,000.
 */
// eslint-disable-next-line statura/no-floating-point-money -- integer × integer (unit values are always whole dollars per s 4AA indexation; no IEEE-754 risk)
export const MAX_CIVIL_PENALTY_INDIVIDUAL = 500 * COMMONWEALTH_PENALTY_UNIT;

/**
 * Returns the s 179(5)(a) maximum civil penalty for a registered provider
 * that is an individual, in dollars, for conduct on the given date.
 */
export function maxCivilPenaltyIndividualFor(date: Date): number {
  return 500 * commonwealthPenaltyUnitFor(date);
}
